/**
 * Hero overlay editor — visual drag canvas + property sidebar.
 * Used inside the HeroEditor in SectionEditor.js.
 */
import { useState } from 'react';
import { Plus, Trash2, Image as ImageIcon, Type } from 'lucide-react';
import HeroOverlays from '../../../components/home/HeroOverlays';
import { resolveImg } from '../../../components/home/HomeSections';

const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <label className="text-[11px] font-medium text-slate-600 uppercase tracking-wide">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#2a9d8f]/30"
  />
);

const ColorInput = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5">
    <input
      type="color"
      value={value || '#ffffff'}
      onChange={(e) => onChange(e.target.value)}
      className="w-10 h-8 rounded border border-slate-200 cursor-pointer flex-shrink-0"
    />
    <Input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const newOverlay = (type) => {
  const id = `ov-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  if (type === 'image') {
    return { id, type, content: '', x_percent: 30, y_percent: 50, width_percent: 20, rotation: 0, border_radius: 0 };
  }
  return {
    id, type: 'text', content: 'SPARA 20%',
    x_percent: 25, y_percent: 30, width_percent: 14, rotation: -8,
    bg_color: '#ff6b35', text_color: '#ffffff',
    font_size: 22, font_weight: 700,
    padding_x: 24, padding_y: 16,
    border_radius: 12, shape: 'circle',
  };
};

const HeroOverlayEditor = ({ section, update }) => {
  const overlays = section.overlays || [];
  const [selectedId, setSelectedId] = useState(overlays[0]?.id || null);

  const setOverlays = (next) => update('overlays', next);

  const addOverlay = (type) => {
    const ov = newOverlay(type);
    setOverlays([...overlays, ov]);
    setSelectedId(ov.id);
  };

  const updateOverlay = (id, patch) => {
    setOverlays(overlays.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  };

  const removeOverlay = (id) => {
    setOverlays(overlays.filter((o) => o.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const handlePositionChange = (id, x, y) => {
    updateOverlay(id, { x_percent: x, y_percent: y });
  };

  const selected = overlays.find((o) => o.id === selectedId) || null;

  return (
    <div className="space-y-3 pt-3 border-t border-slate-100">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Överlägg på hero</h3>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => addOverlay('text')}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#2a9d8f] bg-[#2a9d8f]/10 rounded hover:bg-[#2a9d8f]/20"
            data-testid="add-text-overlay-btn"
          >
            <Type className="w-3.5 h-3.5" /> Textruta
          </button>
          <button
            type="button"
            onClick={() => addOverlay('image')}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#2a9d8f] bg-[#2a9d8f]/10 rounded hover:bg-[#2a9d8f]/20"
            data-testid="add-image-overlay-btn"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Bild
          </button>
        </div>
      </div>

      {overlays.length === 0 ? (
        <p className="text-xs text-slate-400 italic px-2 py-3">
          Lägg till en textruta eller bild och dra den vart du vill på hero-sektionen.
        </p>
      ) : (
        <>
          {/* Drag canvas */}
          <div
            className="relative w-full overflow-hidden rounded-lg border border-slate-200 select-none"
            style={{
              aspectRatio: '16/7',
              backgroundColor: section.bg_color || '#1e3a5f',
              backgroundImage: section.bg_image ? `url(${resolveImg(section.bg_image)})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            data-testid="hero-overlay-canvas"
          >
            <HeroOverlays
              overlays={overlays}
              editable={true}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onPositionChange={handlePositionChange}
            />
            <div className="absolute top-2 left-2 text-[10px] bg-black/50 text-white px-2 py-0.5 rounded pointer-events-none">
              Dra för att placera • Klicka för att markera
            </div>
          </div>

          {/* Overlay list */}
          <div className="flex flex-wrap gap-1.5">
            {overlays.map((ov) => (
              <button
                key={ov.id}
                type="button"
                onClick={() => setSelectedId(ov.id)}
                className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors ${
                  selectedId === ov.id
                    ? 'border-[#2a9d8f] bg-[#2a9d8f]/10 text-[#2a9d8f]'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
                }`}
                data-testid={`overlay-item-${ov.id}`}
              >
                {ov.type === 'text' ? <Type className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                <span className="truncate max-w-[100px]">{ov.type === 'text' ? (ov.content || '(tom)') : 'Bild'}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeOverlay(ov.id); }}
                  className="hover:text-red-500"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </button>
            ))}
          </div>

          {/* Property panel for selected overlay */}
          {selected && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-3" data-testid="overlay-properties">
              {selected.type === 'text' ? (
                <Field label="Text">
                  <Input value={selected.content || ''} onChange={(e) => updateOverlay(selected.id, { content: e.target.value })} placeholder="t.ex. SPARA 20%" />
                </Field>
              ) : (
                <Field label="Bild-URL">
                  <Input value={selected.content || ''} onChange={(e) => updateOverlay(selected.id, { content: e.target.value })} placeholder="https://..." />
                </Field>
              )}

              <Field label="Länka till (klickbar)" hint="Lämna tomt om överlägget inte ska vara klickbart. Externa länkar (https://...) öppnas i ny flik.">
                <Input
                  value={selected.link || ''}
                  onChange={(e) => updateOverlay(selected.id, { link: e.target.value })}
                  placeholder="/produkter eller https://..."
                  data-testid="overlay-link-input"
                />
              </Field>

              <div className="grid grid-cols-3 gap-2">
                <Field label="X (%)">
                  <Input type="number" min="0" max="100" value={Math.round(selected.x_percent ?? 50)} onChange={(e) => updateOverlay(selected.id, { x_percent: parseFloat(e.target.value) || 0 })} />
                </Field>
                <Field label="Y (%)">
                  <Input type="number" min="0" max="100" value={Math.round(selected.y_percent ?? 50)} onChange={(e) => updateOverlay(selected.id, { y_percent: parseFloat(e.target.value) || 0 })} />
                </Field>
                <Field label="Bredd (%)">
                  <Input type="number" min="2" max="100" value={selected.width_percent ?? 20} onChange={(e) => updateOverlay(selected.id, { width_percent: parseFloat(e.target.value) || 20 })} />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Field label="Rotation (grader)">
                  <Input type="number" min="-180" max="180" value={selected.rotation ?? 0} onChange={(e) => updateOverlay(selected.id, { rotation: parseFloat(e.target.value) || 0 })} />
                </Field>
                <Field label="Hörn-radie (px)">
                  <Input type="number" min="0" max="200" value={selected.border_radius ?? 0} onChange={(e) => updateOverlay(selected.id, { border_radius: parseInt(e.target.value, 10) || 0 })} />
                </Field>
              </div>

              {selected.type === 'text' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Bakgrundsfärg">
                      <ColorInput value={selected.bg_color} onChange={(v) => updateOverlay(selected.id, { bg_color: v })} />
                    </Field>
                    <Field label="Textfärg">
                      <ColorInput value={selected.text_color} onChange={(v) => updateOverlay(selected.id, { text_color: v })} />
                    </Field>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Fontstorlek (px)">
                      <Input type="number" min="8" max="120" value={selected.font_size ?? 18} onChange={(e) => updateOverlay(selected.id, { font_size: parseInt(e.target.value, 10) || 18 })} />
                    </Field>
                    <Field label="Fontvikt">
                      <select value={selected.font_weight || 700} onChange={(e) => updateOverlay(selected.id, { font_weight: parseInt(e.target.value, 10) })} className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm bg-white">
                        <option value={400}>Normal</option>
                        <option value={600}>Semibold</option>
                        <option value={700}>Bold</option>
                        <option value={900}>Black</option>
                      </select>
                    </Field>
                    <Field label="Form">
                      <select value={selected.shape || 'rounded'} onChange={(e) => updateOverlay(selected.id, { shape: e.target.value })} className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm bg-white">
                        <option value="rounded">Rektangel</option>
                        <option value="circle">Cirkel</option>
                      </select>
                    </Field>
                  </div>
                </>
              )}

              <button
                type="button"
                onClick={() => removeOverlay(selected.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100"
              >
                <Trash2 className="w-3.5 h-3.5" /> Ta bort detta överlägg
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HeroOverlayEditor;
